import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  CircularProgress,
  Fab,
} from '@mui/material';
import {
  Refresh,
} from '@mui/icons-material';
import { appStore } from '../stores/AppStore';
import DeviceCard from './DeviceCard';
import { trackButtonClick, trackDeviceAction, trackNavigation } from '../utils/analytics';

interface DevicesPageProps {
  onBack: () => void;
  onDeviceClick?: (device: any) => void;
  onLogout?: () => Promise<void> | void;
}

const DevicesPage: React.FC<DevicesPageProps> = observer(({ onBack, onDeviceClick, onLogout }) => {
  // Load devices when page opens
  useEffect(() => {
    appStore.loadDevices();
  }, []);

  const handleRefreshDevices = () => {
    trackButtonClick('Refresh Devices', 'Devices Page');
    appStore.loadDevices();
  };

  const handleDeviceSelect = (device: any) => {
    trackButtonClick('Select Device', 'Devices Page');
    trackDeviceAction('select', device.deviceType);
    if (onDeviceClick) {
      // Navigate to Send page with selected device
      onDeviceClick(device);
    } else {
      // Fallback: just set selected device
      appStore.setSelectedDevice(device);
    }
  };

  const handleRenameDevice = (deviceId: string, newName: string) => {
    trackButtonClick('Rename Device', 'Devices Page');
    trackDeviceAction('rename', 'device');
    appStore.renameDevice(deviceId, newName);
  };

  const handleDeleteCurrentDevice = async (deviceId: string) => {
    trackButtonClick('Delete Current Device', 'Devices Page');
    trackDeviceAction('delete', 'current_device');
    try {
      await appStore.deleteCurrentDevice(deviceId, () => {
        // After successful deletion, sign out
        if (onLogout) {
          onLogout();
        }
      });
    } catch (err: any) {
      appStore.showToast(err.message || 'Failed to delete device');
    }
  };

  const handleDeleteOtherDevice = async (deviceId: string) => {
    trackButtonClick('Delete Other Device', 'Devices Page');
    trackDeviceAction('delete', 'other_device');
    try {
      await appStore.deleteOtherDevice(deviceId);
    } catch (err: any) {
      appStore.showToast(err.message || 'Failed to delete device');
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        py: 1,
        px: 1,
        maxWidth: 1200,
        mx: 'auto',
        width: '100%',
        position: 'relative',
        pb: 5, // Add padding bottom to prevent content from being hidden behind FAB
      }}
    >
      {/* Current Device Section */}
      {appStore.currentDevice && (
        <Card sx={{ backgroundColor: 'rgba(33, 150, 243, 0.2)', backdropFilter: 'blur(10px)', mb: 1 }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 0.5 }}>
              Current Device
            </Typography>
            <DeviceCard
              device={appStore.currentDevice}
              isSelected={appStore.selectedDevice?.id === appStore.currentDevice.id}
              onClick={() => {}} // Prevent clicking on current device
              onRename={handleRenameDevice}
              onDelete={handleDeleteCurrentDevice}
              isCurrentDevice={true}
              hasNewMessages={appStore.hasNewMessages}
            />
          </CardContent>
        </Card>
      )}

      {/* Other Devices Section */}
      <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.12)', backdropFilter: 'blur(10px)' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ color: '#FFFFFF', fontWeight: 'bold', mb: 1 }}>
            Other Devices
          </Typography>

          {appStore.isLoadingDevices ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress />
            </Box>
          ) : appStore.otherDevices.length === 0 ? (
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)', textAlign: 'center', py: 2 }}>
              No other devices found
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {appStore.otherDevices.map((device) => (
                <DeviceCard
                  key={device.id}
                  device={device}
                  isSelected={appStore.selectedDevice?.id === device.id}
                  onClick={() => handleDeviceSelect(device)}
                  onDelete={handleDeleteOtherDevice}
                  isCurrentDevice={false}
                />
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Fixed Refresh Button - Bottom Right */}
      <Fab
        color="primary"
        aria-label="refresh"
        onClick={handleRefreshDevices}
        disabled={appStore.isLoadingDevices}
        sx={{
          position: 'fixed',
          bottom: 12,
          right: 12,
          backgroundColor: '#2196F3',
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: '#1976D2',
          },
          '&:disabled': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            color: 'rgba(255, 255, 255, 0.5)',
          },
          zIndex: 1000,
        }}
      >
        {appStore.isLoadingDevices ? (
          <CircularProgress size={24} sx={{ color: '#FFFFFF' }} />
        ) : (
          <Refresh />
        )}
      </Fab>
    </Box>
  );
});

export default DevicesPage;

